import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  // `whitespace-nowrap` : un badge de statut ne doit JAMAIS passer à la ligne —
  // sinon `rounded-full` transforme le texte multi-lignes en pastille ronde
  // (« blob ») dans les colonnes étroites. Correctif global (source unique) :
  // s'applique à toutes les listes de statuts de l'app.
  "inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground",
        warning:
          "border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-300",
        // Pendant vert de `warning`, même construction (teinte claire + texte encré,
        // inversée en sombre). Sert à marquer ce qui est FOURNI et sain — un modèle
        // Argalis dans une liste, par exemple — sans crier comme `default`.
        success:
          "border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-300",
        // Pendant ROUGE de `success`/`warning`, même construction. À ne pas
        // confondre avec `destructive`, qui est plein : celui-là CRIE, et c'est
        // ce qu'on veut d'une alerte ou d'une action dangereuse. Un ÉTAT de
        // ligne, lui, se répète sur chaque ligne d'un tableau — en plein, une
        // colonne « Annulée » devient un mur rouge qui écrase la lecture du
        // reste. D'où la teinte claire, lisible sans hurler.
        danger:
          "border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-300",
        outline: "text-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <div ref={ref} className={cn(badgeVariants({ variant }), className)} {...props} />
    )
  }
)
Badge.displayName = "Badge"

// eslint-disable-next-line react-refresh/only-export-components
export { Badge, badgeVariants }
